import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
} from '@angular/core';

import { AppState } from 'src/app/reducers';
import { FormProperty } from 'src/app/core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { WidgetSettingsComponent } from '../widget-settings/widget-settings.component';
import { WidgetSettingsService } from 'src/app/core/services/widget.settings.service';

@Component({
  selector: 'app-widget-settings-chart-layout',
  templateUrl: './widget-settings-chart-layout.component.html',
  styleUrls: ['./widget-settings-chart-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WidgetSettingsChartLayoutComponent extends WidgetSettingsComponent {
  constructor(
    protected formService: FormsService,
    protected widgetSettings: WidgetSettingsService,
    protected cdf: ChangeDetectorRef,
    private translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
  ) {
    super(formService, widgetSettings, cdf, store, logger);
  }

  formProperties: FormProperty[] = [
    {
      propertyName: 'settings.pie_view_type',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.pie-style',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.pie-chart-settings',
        style: 'pie-chart-set',
      },
      controlType: 'select',
      columnIndex: 0,
      additionalConfig: {
        placeholder: this.translateService.instant(
          'widget-settings-chart-layout.placeholder.pie-style',
        ),
      },
      valueOptions: this.formService.pieViewTypeOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.pie_rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.pie-rotation',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.pie-chart-settings',
        style: 'pie-chart-set',
      },
      controlType: 'input-number',
      columnIndex: 1,
      additionalConfig: {
        placeholder: this.translateService.instant(
          'widget-settings-chart-layout.placeholder.pie-rotation',
        ),
        min: -360,
        max: 360,
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.pie_total_radius',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.pie-total-radius',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.pie-chart-settings',
        style: 'pie-chart-set',
      },
      controlType: 'input-number',
      columnIndex: 0,
      additionalConfig: {
        placeholder: this.translateService.instant(
          'widget-settings-chart-layout.placeholder.pie-total-radius',
        ),
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.pie_semi_circle_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.pie-inner-radius',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.pie-chart-settings',
        style: 'pie-chart-set',
      },
      controlType: 'input-number',
      columnIndex: 1,
      additionalConfig: {
        placeholder: this.translateService.instant(
          'widget-settings-chart-layout.placeholder.pie-semi-circle-size',
        ),
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_chart_title',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.show-chart-title',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.autoYesNoOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-font-size',
      ),
      controlType: 'select',

      valueOptions: this.formService.fontSizeOptions,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-color',
      ),
      columnIndex: 1,
      controlType: 'colorpicker',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.h_align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-horizontal-align',
      ),
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.v_align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-vertical-align',
      ),
      controlType: 'select',
      columnIndex: 1,
      valueOptions: this.formService.itemVerticalAlignOptions,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.padding_top',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-padding-top',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.padding_bottom',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-padding-bottom',
      ),
      controlType: 'input-number',
      columnIndex: 1,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.padding_left',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-padding-left',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.padding_right',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-padding-right',
      ),
      controlType: 'input-number',
      columnIndex: 1,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.title.margin_bottom',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.title-margin-bottom',
      ),
      controlType: 'input-number',
      columnIndex: 1,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-title-settings',
        style: 'chart-title-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.spacing_top',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-spacing-top',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-spacing-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.spacing_bottom',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-spacing-bottom',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-spacing-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.spacing_left',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-spacing-left',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-spacing-settings',
      },
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.spacing_right',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-spacing-right',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-spacing-settings',
      },
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.margin_top',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-margin-top',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-margin-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.margin_bottom',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-margin-bottom',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-margin-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.margin_left',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-margin-inline-start',
      ),
      controlType: 'input-number',
      columnIndex: 1,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-margin-settings',
      },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart.margin_right',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-margin-inline-end',
      ),
      controlType: 'input-number',
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-spacing-settings',
        style: 'chart-margin-settings',
      },
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart_background_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-background-color',
      ),
      controlType: 'colorpicker',
      columnIndex: 0,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-plot-settings',
        style: 'chart-margin-settings',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.plot_background_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.plot-background-color',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-plot-settings',
        style: 'general-chart-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart_border_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-border-color',
      ),
      controlType: 'colorpicker',
      columnIndex: 0,
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-plot-settings',
        style: 'chart-plot-settings',
      },
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.plot_border_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.plot-border-color',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-plot-settings',
        style: 'chart-plot-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.chart_border_line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.chart-border-line-width',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-plot-settings',
        style: 'chart-plot-settings',
      },
      controlType: 'input-number',
      columnIndex: 0,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.plot_border_line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.plot-border-line-width',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.chart-plot-settings',
        style: 'chart-plot-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_chart_legend',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.show-chart-legend',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.autoYesNoOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.layout',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-layout',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'select',
      columnIndex: 1,
      valueOptions: this.formService.legendLayoutOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.h_align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-horizontal-align',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.v_align',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-vertical-align',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      columnIndex: 1,
      controlType: 'select',
      valueOptions: this.formService.itemVerticalAlignOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.item_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-item-width',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.symbol_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-symbol-size',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.fontSizeOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-font-size',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'select',
      columnIndex: 1,
      valueOptions: this.formService.fontSizeOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.background_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-background-color',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.legend.labels_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-chart-layout.label.legend-labels-color',
      ),
      propertyGroup: {
        key: 'widget-settings-chart-layout.group.legend-settings',
        style: 'legend-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 0,
      isMassUpdate: true,
    },
  ];
}
